"use client";

import { ReactNode, useEffect, useRef, useState } from "react";

type DropdownPosition =
  | "bottom-left"
  | "bottom-right"
  | "top-left"
  | "top-right";

interface DropdownProps {
  trigger: ReactNode;
  position?: DropdownPosition;
  className?: string;
  children: ReactNode;
}

const positionStyles: Record<DropdownPosition, string> = {
  "bottom-left": "top-full left-0 mt-2",
  "bottom-right": "top-full right-0 mt-2",
  "top-left": "bottom-full left-0 mb-2",
  "top-right": "bottom-full right-0 mb-2",
};

export function Dropdown({
  trigger,
  position = "bottom-left",
  className = "",
  children,
}: DropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div ref={dropdownRef} className="relative inline-block">
      <div onClick={() => setIsOpen(!isOpen)}>{trigger}</div>

      {isOpen && (
        <div
          className={`absolute z-50 min-w-[200px] bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl shadow-lg p-2 animate-fadeIn ${positionStyles[position]} ${className}`}
        >
          {children}
        </div>
      )}
    </div>
  );
}

export type { DropdownProps };
